/** Anything these helpers accept where a date is expected: a Date, a date string, or one of the
 * keywords 'now' / 'today'. Missing values fall through to the fallback. */
export type DateInput = Date | string | null | undefined

/**
 * Takes a value and returns a Date instance
 */
export function toDate(value: DateInput, fallback: DateInput = undefined) {
  if (value instanceof Date) {
    return value
  }

  if (typeof value === 'string') {
    switch (value) {
      case 'now':
        return new Date()
      case 'today': {
        const date = new Date()
        date.setHours(0, 0, 0, 0)
        return date
      }
      default:
        return new Date(value)
    }
  }

  return toDate(fallback, 'now')
}

/**
 * Takes a value and returns a Date instance where the time has been reset to zero
 */
export function toDay(value: DateInput, fallback: DateInput = undefined) {
  const day = new Date(toDate(value, fallback))
  day.setHours(0, 0, 0, 0)
  return day
}

/**
 * Takes a value and returns a Date instance where the time has been rounded to an hour
 */
export function toHour(value: DateInput, fallback: DateInput = undefined) {
  const hour = new Date(toDate(value, fallback))
  hour.setMinutes(0, 0, 0)
  return hour
}

/**
 * Takes two values and returns the largest as a Date instance
 */
export function toSmallest(a: DateInput, b: DateInput, fallback: DateInput = undefined) {
  const a_ = toDate(a, fallback)
  const b_ = toDate(b, fallback)
  return a_ < b_ ? a_ : b_
}

/**
 * Takes two values and returns the largest as a Date instance
 */
export function toLargest(a: DateInput, b: DateInput, fallback: DateInput = undefined) {
  const a_ = toDate(a, fallback)
  const b_ = toDate(b, fallback)
  return a_ < b_ ? b_ : a_
}

export function isBetween(value: DateInput, a: DateInput, b: DateInput, fallback = false) {
  if (!value || !a || !b) {
    return fallback
  }
  const date = toDate(value)
  return toSmallest(a, b) <= date && date <= toLargest(a, b)
}

/*
 * Unlike `isBetween` above, `fallback` here is not the boolean result but the date `toDate` falls back
 * to, which is why it is a `DateInput`. No caller passes it; `undefined` and the previous `false` both
 * end up at "now" inside `toDate`.
 */
export function dateInPast(a: DateInput, b: DateInput, fallback: DateInput = undefined) {
  const a_ = new Date(toDate(a, fallback))
  const b_ = new Date(toDate(b, fallback))
  return a_ <= b_
}
